import { type IconName, Combobox as NDSCombobox } from "@narmi/design_system";
import { useRef } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface ComboboxProps<T extends FieldValues> {
  control: Control<T, unknown>;
  name: Path<T>;
  label: string;
  items: { code: string; name: string }[];
  icon?: IconName;
  onSelectionChange?: (value: string) => void;
  onInputValueChange?: (value: string) => void;
}

function Combobox<T extends FieldValues>(props: ComboboxProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Controller
      name={props.name}
      control={props.control}
      render={function ({ field: { onChange, value }, fieldState: { error } }) {
        const selectedItem = props.items.find((item) => item.code === value || item.name === value);
        const inputValue = selectedItem ? selectedItem.name : value || "";
        const isFocusedWithin = () =>
          containerRef.current?.contains(document.activeElement) ?? false;
        // NDS emits an empty update when a free-form value blurs without an explicit clear.
        // Ignore that reset so manually entered values survive blur.
        const shouldIgnoreBlurReset = (nextValue: string) =>
          !nextValue && Boolean(value) && !selectedItem && !isFocusedWithin();

        return (
          <div ref={containerRef}>
            <NDSCombobox
              inputValue={inputValue}
              onChange={(selectedValue: string) => {
                if (shouldIgnoreBlurReset(selectedValue)) {
                  return;
                }

                const matchedItem = props.items.find(
                  (item) => item.name === selectedValue || item.code === selectedValue
                );
                const nextValue = matchedItem?.code ?? selectedValue;

                onChange(nextValue);
                props.onSelectionChange?.(nextValue);
              }}
              onInputChange={(val: string) => {
                props.onInputValueChange?.(val);

                if (val === inputValue) {
                  return;
                }

                if (shouldIgnoreBlurReset(val)) {
                  return;
                }

                const matchedItem = props.items.find(
                  (item) => item.name === val || item.code === val
                );

                onChange(matchedItem?.code ?? val);
              }}
              errorText={error?.message}
              label={props.label}
              icon={props.icon}
            >
              {props.items.map((item) => (
                <NDSCombobox.Item key={item.code} value={item.name} searchValue={item.name}>
                  {item.name}
                </NDSCombobox.Item>
              ))}
            </NDSCombobox>
          </div>
        );
      }}
    />
  );
}

export { Combobox };
